/**
 * Shared server-lint state for the Resume Editor.
 *
 * The Scorecard's Accuracy dimension is the only one that needs the server
 * (`POST /resumes/{name}/review` with `mode: 'lint'` — deterministic, no LLM,
 * ~10ms, free). It is also 45% of the overall score, so a score computed
 * WITHOUT it is a different, higher number.
 *
 * Before this module the lint request lived inside the Scorecard modal, so the
 * editor's rail bar and top-bar chip showed the three-dimension score (e.g. 84)
 * until the user opened the modal — at which point everything dropped to the
 * true number (46). One fetch, one state, three consumers (rail, chip, modal).
 *
 * The state machine is deliberately React-free so the cadence and the failure
 * fallback are unit-testable; `useResumeLint` is the thin hook around it.
 */
import type { LintFinding } from './scorecard';

export interface LintState {
  /** null = never successfully loaded → consumers score 3 dimensions only. */
  findings: LintFinding[] | null;
  /** A request is in flight (a REFRESH keeps the previous findings on screen). */
  loading: boolean;
  /** The last request failed; consumers fall back, they don't break. */
  error: boolean;
}

export const LINT_INITIAL: LintState = { findings: null, loading: false, error: false };

export type LintAction =
  | { type: 'start' }
  | { type: 'ok'; findings: LintFinding[] }
  | { type: 'fail' }
  /** Findings computed elsewhere (the modal's own pass / a per-row recheck). */
  | { type: 'apply'; findings: LintFinding[] | null }
  | { type: 'reset' };

export function lintReducer(state: LintState, action: LintAction): LintState {
  switch (action.type) {
    case 'start':
      // Keep whatever we already have visible while refreshing — the headline
      // score must never bounce back up to the lint-less number mid-session.
      return { ...state, loading: true, error: false };
    case 'ok':
      return { findings: action.findings, loading: false, error: false };
    case 'fail':
      // Fall back to the last good findings (or to null = 3-dimension score).
      return { ...state, loading: false, error: true };
    case 'apply':
      return { findings: action.findings, loading: false, error: false };
    case 'reset':
      return LINT_INITIAL;
    default:
      return state;
  }
}

/**
 * True while the headline score is NOT yet lint-inclusive — the UI shows NO
 * number at all in that window so a soon-to-drop 84 is never on screen.
 *
 * Deliberately keyed on "no first result yet", NOT on "a request is in flight":
 * the editor arms its lint pass a beat after the resume settles, and during that
 * PRE-FLIGHT beat `loading` is still false. Keying on loading let the lint-less
 * number flash exactly as the owner reported (deploytest opened at 94, then
 * dropped to 77 once the pass landed). A refresh is not "computing" — findings
 * are already present, so the four-dimension number on screen stays put — and a
 * FAILED first pass is not either: the three-dimension score is then the answer.
 */
export function isScoreComputing(state: LintState): boolean {
  return state.findings === null && !state.error;
}

/**
 * Placeholder shown WHEREVER the overall score would be, while the first lint
 * pass for a freshly-opened resume is still in flight. Owner decision
 * 2026-07-27: a transiently-wrong number reads as a bug no matter how it is
 * styled, so the overall appears exactly once — when it is correct.
 */
export const SCORE_PENDING_LABEL = '—';

/** The overall score as an integer 0-100 (what the scorecard reports). */
export function clampScore(score: number): number {
  return Math.max(0, Math.min(100, Math.round(score || 0)));
}

/**
 * What the overall score RENDERS as: the number, or the neutral placeholder
 * while it is still being computed. Every overall readout (rail headline,
 * collapsed rail, top-bar chip, modal ring) goes through this so they can never
 * disagree about whether a number is knowable yet.
 */
export function overallScoreLabel(score: number, computing?: boolean): string {
  return computing ? SCORE_PENDING_LABEL : String(clampScore(score));
}

/**
 * Fill percentage for the rail's score bar. Empty (not 84%-full) while the
 * score is pending — a bar drawn at the lint-less number is the same wrong
 * answer told graphically.
 */
export function overallScoreBarPct(score: number, computing?: boolean): number {
  return computing ? 0 : clampScore(score);
}

/**
 * Run one lint pass into a dispatcher. Never throws: a failure dispatches
 * `fail` (consumers keep working on the previous/absent findings). `isCancelled`
 * lets an unmounted / superseded run drop its result on the floor.
 */
export async function runLint(
  fetcher: () => Promise<LintFinding[] | null | undefined>,
  dispatch: (a: LintAction) => void,
  isCancelled: () => boolean = () => false,
): Promise<void> {
  dispatch({ type: 'start' });
  try {
    const findings = await fetcher();
    if (isCancelled()) return;
    dispatch({ type: 'ok', findings: findings || [] });
  } catch {
    if (isCancelled()) return;
    dispatch({ type: 'fail' });
  }
}
